import type { Faro } from "@grafana/faro-web-sdk";

import { normalizeRoutePath } from "./normalize-route-path";

const VITE_FARO_COLLECTOR_URL = import.meta.env.VITE_FARO_COLLECTOR_URL;
const VITE_ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT;
const VITE_API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT;

const APP_NAME = "ark-frontend";
const FARO_BATCH_SEND_INTERVAL_MS = 10_000;

let faroInstance: Faro | null = null;
let isInitialized = false;

function resolveBackendOrigin(): string[] {
  if (!VITE_API_ENDPOINT) {
    return [];
  }
  try {
    return [new URL(VITE_API_ENDPOINT).origin];
  } catch {
    // A malformed VITE_API_ENDPOINT must not crash app bootstrap; disable
    // trace-header propagation instead.
    return [];
  }
}

export async function initFaro(): Promise<void> {
  if (isInitialized || !VITE_FARO_COLLECTOR_URL) {
    return;
  }
  isInitialized = true;

  // Dynamic import keeps the Faro SDK and the OTel web-tracing stack out of the
  // entry chunk: they are fetched and parsed only when a collector URL is set,
  // not on every first page load.
  const { initializeFaro, getWebInstrumentations, LogLevel } = await import("@grafana/faro-web-sdk");
  const { TracingInstrumentation } = await import("@grafana/faro-web-tracing");

  faroInstance = initializeFaro({
    url: VITE_FARO_COLLECTOR_URL,
    app: {
      name: APP_NAME,
      environment: VITE_ENVIRONMENT || "development",
    },
    // Session ids stay in sessionStorage only (no persistent identifier),
    // matching the cookie-less analytics posture on embed routes.
    sessionTracking: {
      enabled: true,
      persistent: false,
    },
    // Traces already record fetch and XHR requests. Resource events duplicate
    // them and make each short poll produce another low-value signal.
    trackResources: false,
    batching: {
      sendTimeout: FARO_BATCH_SEND_INTERVAL_MS,
    },
    instrumentations: [
      ...getWebInstrumentations({ captureConsole: true }),
      new TracingInstrumentation({
        instrumentationOptions: {
          // Propagate the W3C traceparent header only to our own backend so
          // frontend spans join the backend trace in Tempo.
          propagateTraceHeaderCorsUrls: resolveBackendOrigin(),
        },
      }),
    ],
    consoleInstrumentation: {
      // Console noise stays out of Loki; errors are the signal.
      disabledLevels: [LogLevel.TRACE, LogLevel.DEBUG, LogLevel.LOG, LogLevel.INFO, LogLevel.WARN],
    },
  });
}

export function identifyFaroUser(userId: string): void {
  if (!faroInstance) {
    return;
  }
  // Only the opaque user id is sent — no email or name (PII stays in PostHog,
  // which is consent-gated).
  faroInstance.api.setUser({ id: userId });
}

export function pushFaroEvent(name: string, properties?: Record<string, unknown>): void {
  if (!faroInstance) {
    return;
  }
  faroInstance.api.pushEvent(name, properties ? toFaroAttributes(properties) : undefined);
}

// Faro's pushEvent expects a Record<string, string>. Preserve primitives as
// their canonical string form (numbers/booleans keep meaning in Grafana), and
// JSON-encode objects so nested structure is still inspectable in Explore.
function toFaroAttributes(properties: Record<string, unknown>): Record<string, string> {
  const attributes: Record<string, string> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value === null || value === undefined) {
      continue;
    }
    if (typeof value === "object") {
      try {
        attributes[key] = JSON.stringify(value);
      } catch {
        attributes[key] = "[unserializable]";
      }
      continue;
    }
    attributes[key] = String(value);
  }
  return attributes;
}

export function pushFaroError(error: Error, context?: Record<string, string>): void {
  if (!faroInstance) {
    return;
  }
  faroInstance.api.pushError(error, { context });
}

export function setFaroView(pathname: string): void {
  if (!faroInstance) {
    return;
  }
  faroInstance.api.setView({ name: normalizeRoutePath(pathname) });
}
